import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import Link from '@mui/material/Link';
import { apiFetch, ApiError } from '@/lib/api-client';
import { useAuthStore, type SessionEndReason, type UknUser } from '@/store/auth';
import { APP_BASE, API_BASE } from '@/lib/api-base';
import { loadGsi } from '@/lib/google-gsi';
import { rememberedWorkspace, rememberWorkspace } from '@/api/signup';
import logoUrl from '@/assets/uknowva-hrms-logo.png';

/** Sign-in requests name a standalone (self-signup) workspace with this header. */
const WORKSPACE_HEADER = 'X-Workspace';

function workspaceHeaders(workspace: string): Record<string, string> | undefined {
  const w = workspace.trim().toLowerCase();
  return w ? { [WORKSPACE_HEADER]: w } : undefined;
}

const schema = z.object({
  username: z.string().min(1, 'Required'),
  password: z.string().min(1, 'Required'),
});
type FormValues = z.infer<typeof schema>;

type LoginResponse = { access_token: string; user: UknUser };

const END_REASON_MESSAGE: Record<SessionEndReason, string | null> = {
  'expired': 'Your session expired. Sign in again to pick up where you left off.',
  'signed-out-elsewhere': 'You were signed out in another tab. Sign in again to continue.',
  'signed-out': null,
};

/** The origin of the page that sent the browser here, when it is not payroll
 *  itself — for a `?ntk=` arrival, the uKnowva the user clicked through from. */
function crossOriginReferrer(): string | null {
  try {
    if (!document.referrer) return null;
    const origin = new URL(document.referrer).origin;
    return origin === window.location.origin ? null : origin;
  } catch {
    return null;
  }
}

/** Where to send the user after sign-in: the page their session ended on
 *  (AppShell passes it as `state.from`), query string included, else home. */
function returnPath(state: unknown): string {
  const from = (state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  if (!from?.pathname || from.pathname.endsWith('/login')) return `${APP_BASE}/`;
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

export function LoginPage() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const setSession = useAuthStore((s) => s.setSession);
  const endReason = useAuthStore((s) => s.endReason);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('sso_error');
  const ntk = searchParams.get('ntk');
  const [serverError, setServerError] = useState<string | null>(null);
  // Reveal-password toggle. Always starts masked, and is forced back to masked
  // on every submit: a failed sign-in leaves the user staring at an error on a
  // page that would otherwise still be showing their password in clear text.
  const [showPassword, setShowPassword] = useState(false);

  // ── Workspace ID (standalone workspaces created by self-service signup) ──
  // Shown only when this server has such workspaces, or when a link names
  // one. Blank keeps the sign-in exactly as it was: the server's default
  // workspace, or the uKnowva SSO flows.
  const workspaceParam = (searchParams.get('workspace') ?? '').trim().toLowerCase();
  const [workspace, setWorkspace] = useState<string>(() => workspaceParam || rememberedWorkspace());
  // The value the Google button was configured for — updated on blur, not per keystroke.
  const [committedWorkspace, setCommittedWorkspace] = useState<string>(() => workspaceParam || rememberedWorkspace());
  const [workspaceLogin, setWorkspaceLogin] = useState<boolean>(!!workspaceParam);
  const [signupOpen, setSignupOpen] = useState(false);
  const showWorkspace = workspaceLogin || !!workspaceParam;

  useEffect(() => {
    if (isAuthed || ntk) return;
    fetch(`${API_BASE}/signup/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c: { enabled?: boolean; workspace_login?: boolean } | null) => {
        if (!c) return;
        setSignupOpen(!!c.enabled);
        if (c.workspace_login) setWorkspaceLogin(true);
      })
      .catch(() => { /* older server or Master DB down: plain login, as before */ });
  }, [isAuthed, ntk]);

  useEffect(() => {
    if (!ntk || isAuthed) return;
    // uKnowva passed ?ntk=<session_token> — redirect to our SSO endpoint
    // which will callback to uKnowva, validate, and auto-login.
    //
    // That navigation starts HERE, so the endpoint sees payroll as its
    // Referer and cannot tell which uKnowva issued the token. Pass it along
    // as instance= — from the link itself, else this page's own referrer.
    // Unsigned on purpose: the server only accepts it if it matches the
    // uKnowva the workspace is paired with.
    const params = new URLSearchParams({ ntk });
    const instance = searchParams.get('instance') || crossOriginReferrer();
    if (instance) params.set('instance', instance);
    window.location.replace(`${API_BASE}/auth/sso/uknowva?${params.toString()}`);
  }, [ntk, isAuthed, searchParams]);

  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '' },
  });

  // ── Google Sign-In ─────────────────────────────────────────────
  const googleBtnRef = useRef<HTMLDivElement | null>(null);
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    if (isAuthed || ntk) return;
    let cancelled = false;

    const ws = showWorkspace ? committedWorkspace : '';
    setGoogleReady(false);

    // 1. Ask the backend whether this tenant has Google enabled + its client_id.
    fetch(`${API_BASE}/auth/google/config`, { headers: workspaceHeaders(ws) })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((cfg: { enabled?: boolean; client_id?: string }) => {
        if (cancelled || !cfg?.enabled || !cfg.client_id) return;
        // 2. Load GIS and render the button.
        return loadGsi().then(() => {
          if (cancelled || !window.google?.accounts?.id) return;
          window.google.accounts.id.initialize({
            client_id: cfg.client_id,
            callback: async (resp: { credential?: string }) => {
              if (!resp?.credential) return;
              setServerError(null);
              try {
                const res = await apiFetch<LoginResponse>('/auth/google', {
                  method: 'POST',
                  body: JSON.stringify({ credential: resp.credential }),
                  headers: workspaceHeaders(ws),
                });
                if (ws) rememberWorkspace(ws);
                setSession(res.access_token, res.user);
                navigate(returnPath(location.state), { replace: true });
              } catch (e) {
                setServerError(e instanceof ApiError ? e.detailMessage : 'Google sign-in failed.');
              }
            },
          });
          if (googleBtnRef.current) {
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              theme: 'outline', size: 'large', width: 372, text: 'signin_with',
            });
            setGoogleReady(true);
          }
        });
      })
      .catch(() => { /* Google config unreachable → button just stays hidden. */ });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, ntk, committedWorkspace, showWorkspace]);

  // Also the path taken right after a successful sign-in: setSession
  // re-renders this page before navigate() lands, so this redirect has to
  // agree with it or it wins the race and drops the user on the dashboard.
  if (isAuthed) {
    return <Navigate to={returnPath(location.state)} replace />;
  }

  if (ntk) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
                 alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
        <CircularProgress size={32} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Signing you in via uKnowva SSO…
        </Typography>
      </Box>
    );
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setShowPassword(false);
    const ws = showWorkspace ? workspace.trim().toLowerCase() : '';
    try {
      const res = await apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(values),
        headers: workspaceHeaders(ws),
      });
      if (showWorkspace) rememberWorkspace(ws);
      setSession(res.access_token, res.user);
      navigate(returnPath(location.state), { replace: true });
    } catch (e) {
      const code = e instanceof ApiError ? (e.body as { code?: string } | null)?.code : undefined;
      if (showWorkspace && !ws && code === 'DIRECT_LOGIN_UNCONFIGURED') {
        setServerError('Enter your workspace ID to sign in.');
      } else if (e instanceof ApiError) setServerError(e.detailMessage);
      else setServerError('Login failed — please try again.');
    }
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        // Elegant branded ground: soft blue wash with a diagonal accent glow.
        background:
          'radial-gradient(1200px 600px at 100% 0%, rgba(16,138,230,0.14), transparent 55%),' +
          'radial-gradient(900px 500px at 0% 100%, rgba(16,138,230,0.10), transparent 50%),' +
          'linear-gradient(135deg, #eef5fd 0%, #f4f7fb 50%, #eaf1f9 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4.5 }, maxWidth: 428, width: '100%',
          borderRadius: 3, border: 1, borderColor: 'rgba(16,138,230,0.14)',
          boxShadow: '0 18px 48px rgba(24,40,72,0.14)',
          animation: 'uknLoginIn 320ms cubic-bezier(0.16,1,0.3,1)',
          '@keyframes uknLoginIn': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'none' },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box component="img" src={logoUrl} alt="uKnowva"
            sx={{ height: 40, width: 'auto', display: 'block' }} />
        </Box>
        <Typography variant="h2" sx={{ textAlign: 'center', mb: 0.5 }}>uKnowva Payroll</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
          Sign in to continue
        </Typography>

        {endReason && END_REASON_MESSAGE[endReason] && !ssoError && !serverError && (
          <Alert severity="info" sx={{ mb: 2 }}>{END_REASON_MESSAGE[endReason]}</Alert>
        )}

        {ssoError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            SSO login failed: {decodeURIComponent(ssoError)}
          </Alert>
        )}

        <form onSubmit={onSubmit} noValidate>
          {showWorkspace && (
            <TextField
              label="Workspace ID"
              value={workspace}
              onChange={(e) => setWorkspace(e.target.value.toLowerCase().replace(/[\s-]+/g, '_'))}
              onBlur={() => setCommittedWorkspace(workspace.trim().toLowerCase())}
              autoComplete="organization"
              autoFocus={!workspace}
              placeholder="your_company"
              InputLabelProps={{ shrink: true }}
              helperText="From your workspace welcome email. Leave blank if you sign in through uKnowva."
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            label="Username"
            autoFocus={!showWorkspace || !!workspace}
            autoComplete="username"
            placeholder="admin"
            InputLabelProps={{ shrink: true }}
            error={!!formState.errors.username}
            helperText={formState.errors.username?.message}
            sx={{ mb: 2 }}
            {...register('username')}
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            InputLabelProps={{ shrink: true }}
            error={!!formState.errors.password}
            helperText={formState.errors.password?.message}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                    <IconButton
                      edge="end"
                      size="small"
                      // Names the ACTION, not the state — a screen reader user
                      // needs to know what pressing it does. `aria-pressed`
                      // carries the state.
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      // mousedown steals focus from the field before the click
                      // lands, so the caret would jump out mid-typing.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword
                        ? <VisibilityOffIcon fontSize="small" />
                        : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            {...register('password')}
          />
          {serverError && (
            <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>
          )}
          <Button type="submit" variant="contained" fullWidth disabled={formState.isSubmitting}>
            {formState.isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        {/* ── Google Sign-In (rendered only when the tenant enables it) ── */}
        <Box sx={{ display: googleReady ? 'block' : 'none', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <Typography variant="caption" color="text.secondary">or</Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>
          <Box ref={googleBtnRef} sx={{ display: 'flex', justifyContent: 'center' }} />
        </Box>

        {signupOpen && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2.5 }}>
            New to uKnowva Payroll?{' '}
            <Link component={RouterLink} to={`${APP_BASE}/signup`}>Create a workspace</Link>
          </Typography>
        )}

      </Paper>
    </Box>
  );
}
